import {
  Button,
  type ButtonProps,
  HStack,
  Heading,
  IconButton,
  Text,
  VStack,
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { useModalPaddings } from '../hooks';
import { Modal } from './Modal';
import { DeleteIcon } from './icons';

const DefaultDeleteButton: React.FC<ButtonProps> = ({ onClick, ...other }) => (
  <IconButton
    aria-label='Delete button'
    color='typography.secondary'
    data-testid='delete-data'
    icon={<DeleteIcon boxSize={4} />}
    size='sm'
    onClick={onClick}
    {...other}
  />
);

interface IConfirmPopupProps extends ButtonProps {
  isMobile?: boolean;
  onConfirm: () => void;
  PreviewButton?: React.FC<ButtonProps>;
  header?: string;
  message?: string;
  positiveButtonText?: string;
  negativeButtonText?: string;
  positiveButtonProps?: ButtonProps;
  isOpenOutside?: boolean;
}

const ConfirmPopup: React.FC<IConfirmPopupProps> = ({
  onConfirm,
  PreviewButton = DefaultDeleteButton,
  header = 'Confirmation',
  message = 'Are you sure you want to delete?',
  positiveButtonText = 'Delete',
  negativeButtonText = 'Cancel',
  positiveButtonProps,
  isMobile = false,
  isOpenOutside,
  ...other
}) => {
  const [isOpen, setIsOpen] = useState(isOpenOutside || false);
  const onOpen = () => setIsOpen(!isOpen);
  const onClose = () => setIsOpen(false);

  const modalPadding = useModalPaddings(isMobile);
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = async () => {
    setIsLoading(true);
    await onConfirm();
    setIsLoading(false);
    onClose();
  };

  useEffect(() => {
    if (isOpenOutside) {
      setIsOpen(isOpenOutside);
    }
  }, [isOpenOutside]);

  return (
    <>
      <PreviewButton
        onClick={onOpen}
        {...other}
      />
      <Modal
        isMobile={isMobile}
        isOpen={isOpen}
        onClose={onClose}
      >
        <VStack
          spacing={5}
          {...modalPadding}
        >
          <Heading
            as='div'
            size='md'
            textAlign='center'
          >
            {header}
          </Heading>
          <Text textAlign='center'>{message}</Text>
          <HStack w='100%'>
            <Button
              variant='outline'
              w='100%'
              onClick={onClose}
            >
              {negativeButtonText}
            </Button>
            <Button
              colorScheme='delete'
              data-testid='approve-confirmation'
              isLoading={isLoading}
              w='100%'
              onClick={handleConfirm}
              {...positiveButtonProps}
            >
              {positiveButtonText}
            </Button>
          </HStack>
        </VStack>
      </Modal>
    </>
  );
};

export default ConfirmPopup;
